import { computed, getCurrentScope, onScopeDispose, ref } from 'vue';
// On hover menu opening delay in milliseconds.
export const DEFAULT_OPEN_DELAY = 100;
export const DEFAULT_CLOSE_DELAY = 150;
// Only one menu may be open at a time. This holds the way to close the one that is open
// right now: every menu has its own giveWayToOtherMenu function, so comparing against it
// also answers "is the open menu me?".
let closeOpenMenu = null;
/**
 * Open state for a dropdown menu that can also be opened by hovering.
 *
 * Hover opens the menu, and it closes again when the pointer leaves.
 * A click (or a keypress) pins it, so it stays open until the user
 * picks an item, presses Escape or clicks outside.
 */
export function useMenuOpenState(props) {
    const isHover = computed(() => props.trigger === 'hover');
    const open = ref(false);
    const pinned = ref(false);
    let openTimer;
    let closeTimer;
    // Tells a click on the trigger apart from Escape, an outside click or picking an item.
    let pointerDownOnTrigger = false;
    // True while this menu is closing to make room for another one.
    let closedForAnotherMenu = false;
    const clearTimers = () => {
        clearTimeout(openTimer);
        clearTimeout(closeTimer);
    };
    // Closes this menu because another one is taking over.
    const giveWayToOtherMenu = () => {
        clearTimers();
        closedForAnotherMenu = open.value;
        pinned.value = false;
        open.value = false;
    };
    // Opening this menu closes whichever other one is open.
    const setOpen = (next) => {
        if (next && !isHover.value) {
            if (closeOpenMenu && closeOpenMenu !== giveWayToOtherMenu)
                closeOpenMenu();
            closeOpenMenu = giveWayToOtherMenu;
        }
        else if (closeOpenMenu === giveWayToOtherMenu) {
            closeOpenMenu = null;
        }
        open.value = next;
    };
    const openOnHoverEnter = () => {
        if (!isHover.value)
            return;
        // Coming back to the trigger or moving into the panel cancels a pending close.
        clearTimers();
        if (open.value)
            return;
        openTimer = setTimeout(() => setOpen(true), props.openDelay ?? DEFAULT_OPEN_DELAY);
    };
    const closeOnHoverLeave = () => {
        if (!isHover.value)
            return;
        clearTimers();
        if (pinned.value)
            return;
        closeTimer = setTimeout(() => setOpen(false), props.closeDelay ?? DEFAULT_CLOSE_DELAY);
    };
    // Runs before the click reaches the menu. Button 0 is the left button, so we note that the
    // next open or close comes from the user clicking the trigger. Other buttons (right click)
    // are left alone, they should just dismiss the menu.
    const onTriggerPointerDown = (event) => {
        if (event.button === 0)
            pointerDownOnTrigger = true;
    };
    // Closing a menu puts focus back on its trigger. That focus counts as a click outside the
    // menu we just opened and would close it again, so skip it in that case.
    const onCloseAutoFocus = (event) => {
        if (!closedForAnotherMenu)
            return;
        closedForAnotherMenu = false;
        event.preventDefault();
    };
    // Every open state change the menu itself asks for: trigger click, keyboard,
    // Escape, outside click or picking an item.
    const onOpenChange = (next) => {
        const fromTriggerClick = pointerDownOnTrigger;
        pointerDownOnTrigger = false;
        clearTimers();
        // Clicking a menu that hover opened keeps it open and pins it. Closing it here would
        // look like the click broke the menu: nothing reopens it until the pointer comes back.
        if (!next && fromTriggerClick && !pinned.value) {
            pinned.value = true;
            setOpen(true);
            return;
        }
        pinned.value = next;
        setOpen(next);
    };
    if (getCurrentScope()) {
        onScopeDispose(() => {
            clearTimers();
            if (closeOpenMenu === giveWayToOtherMenu)
                closeOpenMenu = null;
        });
    }
    return {
        open,
        isHover,
        setOpen,
        // A menu the user only hovered must not steal focus. A clicked one may.
        shouldFocusOnOpen: computed(() => !isHover.value || pinned.value),
        onOpenChange,
        onCloseAutoFocus,
        onTriggerPointerDown,
        onTriggerPointerEnter: openOnHoverEnter,
        onTriggerPointerLeave: closeOnHoverLeave,
        onContentPointerEnter: openOnHoverEnter,
        onContentPointerLeave: closeOnHoverLeave,
    };
}
